interface SessionInfoResponse {
  csrfTokenValue: string;
}

interface CookieConsentResponse {
  necessary: boolean;
  statistics: boolean;
  marketing: boolean;
}

interface CookieConsentRefs {
  consentForm: HTMLFormElement;
  csrfToken: HTMLInputElement;
  success?: HTMLElement;
}

interface AlpineContext {
  $refs: CookieConsentRefs;
  $root: HTMLElement;
  $watch: (property: string, callback: () => void) => void;
  $nextTick: (callback: () => void) => void;
}

interface CookieConsentState {
  consent: CookieConsentResponse | null;
  showCookieBar: boolean;
  showGroups: boolean;
  groupsAreHidden: boolean;
  init: (this: CookieConsentState & AlpineContext) => void;
  applyConsentToCheckboxes: (this: CookieConsentState & AlpineContext) => void;
  toggleGroups: () => void;
  handleConsentSubmit: (
    this: CookieConsentState & AlpineContext,
    e: SubmitEvent,
  ) => void;
}

const toCookieConsentResponse = (value: unknown): CookieConsentResponse | null => {
  const normalizeConsentValue = (consentValue: unknown): boolean | null => {
    if (typeof consentValue === 'boolean') {
      return consentValue;
    }

    if (consentValue === 'on' || consentValue === 'true' || consentValue === 1) {
      return true;
    }

    if (consentValue === 'off' || consentValue === 'false' || consentValue === 0) {
      return false;
    }

    return null;
  };

  if (
    typeof value !== 'object'
    || value === null
    || !('necessary' in value)
    || !('statistics' in value)
    || !('marketing' in value)
  ) {
    return null;
  }

  const consent = value as {
    necessary: unknown;
    statistics: unknown;
    marketing: unknown;
  };

  const necessary = normalizeConsentValue(consent.necessary);
  const statistics = normalizeConsentValue(consent.statistics);
  const marketing = normalizeConsentValue(consent.marketing);

  if (necessary === null || statistics === null || marketing === null) {
    return null;
  }

  return {
    necessary,
    statistics,
    marketing,
  };
};

const getSessionInfo = async (): Promise<SessionInfoResponse> => {
  const response = await fetch('/actions/users/session-info/', {
    headers: {
      Accept: 'application/json',
    },
  });

  const json = await response.json() as unknown;

  return json as SessionInfoResponse;
};

const getConsentFromServer = async (url: string): Promise<CookieConsentResponse | null> => {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  const json = await response.json() as unknown;

  return toCookieConsentResponse(json);
};

const runTracking = () => {
  if (window.trackingRan || typeof dataLayer === 'undefined') {
    return;
  }

  if (window.ccc !== null && window.ccc.necessary === true) {
    dataLayer.push({ event: 'cookieconsent_preferences' });
  }

  if (window.ccc !== null && window.ccc.statistics === true) {
    dataLayer.push({ event: 'cookieconsent_statistics' });
  }

  if (window.ccc !== null && window.ccc.marketing === true) {
    dataLayer.push({ event: 'cookieconsent_marketing' });
  }

  window.trackingRan = true;
};

const setCheckboxChecked = (input: HTMLInputElement, value: boolean): void => {
  const checkbox = input;
  checkbox.checked = value;
};

const getGroupCheckbox = (
  form: HTMLFormElement,
  slug: keyof CookieConsentResponse,
): HTMLInputElement | null => form.querySelector<HTMLInputElement>(`input[name="group-${slug}"]`);

const refresh = (ms: number): void => {
  setTimeout(() => {
    window.location.reload();
  }, ms);
};

function addWindowObject(consent: CookieConsentResponse | null) {
  if (consent === null) {
    window.ccc = null;
  } else {
    window.ccc = consent;
    runTracking();
  }
}

const getFormParams = (form: HTMLFormElement): URLSearchParams => {
  const params = new URLSearchParams();

  new FormData(form).forEach((value, key) => {
    if (typeof value === 'string') {
      params.append(key, value);
    }
  });

  return params;
};

export default function initCookieConsent(): void {
  Alpine.data('cookieConsent', (): CookieConsentState => ({
    consent: null,
    showCookieBar: false,
    showGroups: false,
    groupsAreHidden: true,

    init() {
      const form = this.$refs.consentForm;

      getSessionInfo().then((session) => {
        const csrfInput = this.$refs.csrfToken;
        csrfInput.value = session.csrfTokenValue;
      });

      const rawConsent = form.dataset.consent ?? 'null';
      let fallbackConsent: CookieConsentResponse | null = null;

      try {
        fallbackConsent = toCookieConsentResponse(JSON.parse(rawConsent) as unknown);
      } catch {
        fallbackConsent = null;
      }

      this.consent = fallbackConsent;

      if (this.consent === null) {
        this.showCookieBar = true;
      } else {
        this.applyConsentToCheckboxes();
      }

      addWindowObject(this.consent);

      const showUrl = form.dataset.show;
      if (showUrl) {
        getConsentFromServer(showUrl).then((serverConsent) => {
          this.consent = serverConsent;

          if (this.consent === null) {
            this.showCookieBar = true;
          } else {
            this.showCookieBar = false;
            this.applyConsentToCheckboxes();
          }

          addWindowObject(this.consent);
        }).catch(() => {
          // Keep fallback consent state when the refresh endpoint is unavailable.
        });
      }

      this.$watch('showCookieBar', () => {
        if (this.showCookieBar) {
          this.$root.setAttribute('role', 'dialog');

          const acceptBtn: HTMLButtonElement | null = document.querySelector(
            'button[data-action="accept"]',
          );
          const disclaimer = document.getElementById('disclaimer');

          if (acceptBtn && (!disclaimer?.checkVisibility())) {
            this.$nextTick(() => acceptBtn.focus());
          }
        } else {
          this.$root.removeAttribute('role');
        }
      });
    },

    applyConsentToCheckboxes() {
      if (this.consent === null) {
        return;
      }

      const form = this.$refs.consentForm;

      const setCheckbox = (slug: keyof CookieConsentResponse, value: boolean) => {
        const checkbox = getGroupCheckbox(form, slug);

        if (checkbox && !checkbox.disabled) {
          setCheckboxChecked(checkbox, value);
        }
      };

      setCheckbox('necessary', this.consent.necessary);
      setCheckbox('statistics', this.consent.statistics);
      setCheckbox('marketing', this.consent.marketing);
    },

    toggleGroups() {
      this.showGroups = !this.showGroups;
      this.groupsAreHidden = !this.groupsAreHidden;
    },

    handleConsentSubmit(e: SubmitEvent) {
      e.preventDefault();
      this.showCookieBar = false;

      const submitBtn = e.submitter;
      const form = this.$refs.consentForm;
      const params = getFormParams(form);

      const checkboxes: NodeListOf<HTMLInputElement> = form.querySelectorAll(
        'input[type="checkbox"]',
      );

      const setGroupValue = (
        queryParams: URLSearchParams,
        slug: keyof CookieConsentResponse,
      ): void => {
        const checkbox = getGroupCheckbox(form, slug);
        queryParams.set(`group-${slug}`, checkbox?.checked ? 'on' : 'off');
      };

      if (submitBtn?.dataset.action === 'accept') {
        params.set('acceptAll', 'true');
        params.set('group-statistics', 'on');
        params.set('group-marketing', 'on');

        checkboxes.forEach((checkbox) => {
          setCheckboxChecked(checkbox, true);
        });
      } else if (submitBtn?.dataset.action === 'decline') {
        checkboxes.forEach((checkbox) => {
          if (!checkbox.disabled) {
            params.set(checkbox.name, 'off');
            setCheckboxChecked(checkbox, false);
          }
        });
      } else if (submitBtn?.dataset.action === 'save') {
        setGroupValue(params, 'statistics');
        setGroupValue(params, 'marketing');
      }

      checkboxes.forEach((checkbox) => {
        if (!checkbox.disabled) {
          params.set(checkbox.name, checkbox.checked ? 'on' : 'off');
        }
      });

      const xhr = new XMLHttpRequest();
      xhr.open('POST', form.dataset.url ?? '');
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.send(params.toString());

      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }

        if (xhr.status !== 200) {
          // eslint-disable-next-line no-console
          console.error(`Cookie consent update failed: ${xhr.status}`, xhr.responseText);
          this.showCookieBar = true;
          addWindowObject(null);
          window.dispatchEvent(new CustomEvent('cookieConsentError', { detail: xhr.status }));
          return;
        }

        const nextConsent: CookieConsentResponse = {
          necessary: true,
          statistics: false,
          marketing: false,
        };

        if (submitBtn?.dataset.action === 'accept') {
          nextConsent.statistics = true;
          nextConsent.marketing = true;
        } else if (submitBtn?.dataset.action === 'save') {
          const statisticsCheck = getGroupCheckbox(form, 'statistics');
          const marketingCheck = getGroupCheckbox(form, 'marketing');

          nextConsent.statistics = statisticsCheck?.checked ?? false;
          nextConsent.marketing = marketingCheck?.checked ?? false;
        }

        this.consent = nextConsent;
        this.applyConsentToCheckboxes();
        addWindowObject(this.consent);

        this.$refs.success?.classList.remove('hidden');

        if (
          form.hasAttribute('data-refresh')
          && form.dataset.refresh === 'true'
          && form.hasAttribute('data-refreshtime')
        ) {
          refresh(parseInt(form.dataset.refreshtime ?? '2000', 10));
        }
      };
    },
  }));
}
